import { useCallback, useEffect, useMemo, useState } from 'react';
import { MoreHorizontal, Pencil, Plus, X } from 'lucide-react';
import { api } from '../../../../services/api';
import { useWorkspace } from '../../WorkspaceContext';
import { useWorkspaceCurrency } from '../../hooks/useWorkspaceCurrency';
import { formatCurrency, formatDuration } from '../../utils/datetime';

const emptyService = (currency = 'USD') => ({
  name: '',
  description: '',
  durationMinutes: 60,
  price: 0,
  currency,
  categoryId: '',
});

const ManageServices = () => {
  const { activeLocation, refresh } = useWorkspace();
  const { currency: defaultCurrency, currencyMeta, hasLocaleOverride, local } =
    useWorkspaceCurrency();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(() => emptyService());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formCategoryName, setFormCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [menuOpenId, setMenuOpenId] = useState(null);

  const categoryNameById = (id) => categories.find((item) => item.id === id)?.name || '';

  const loadServices = useCallback(async () => {
    if (!activeLocation) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [servicesResult, categoriesResult] = await Promise.all([
      api.getWorkspaceServices(activeLocation.id),
      api.getWorkspaceServiceCategories(activeLocation.id),
    ]);
    if (servicesResult.error) {
      setError(servicesResult.error);
      setServices([]);
    } else {
      setError('');
      setServices(Array.isArray(servicesResult.data) ? servicesResult.data : []);
    }
    if (!categoriesResult.error && Array.isArray(categoriesResult.data)) {
      setCategories(categoriesResult.data);
    } else {
      setCategories(activeLocation.serviceCategories || []);
    }
    setLoading(false);
  }, [activeLocation]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useEffect(() => {
    if (!menuOpenId) return undefined;
    const close = (event) => {
      if (event.target.closest?.('[data-service-menu]')) return;
      setMenuOpenId(null);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuOpenId(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpenId]);

  const openCreate = () => {
    setEditingId(null);
    const next = emptyService(defaultCurrency);
    if (selectedCategoryId !== 'all' && selectedCategoryId !== 'uncategorized') {
      next.categoryId = selectedCategoryId;
    }
    setForm(next);
    setFormCategoryName('');
    setFormOpen(true);
  };

  const openEdit = (service) => {
    setEditingId(service.id);
    setForm({
      name: service.name || '',
      description: service.description || '',
      durationMinutes: service.durationMinutes || 60,
      price: service.price || 0,
      currency: defaultCurrency,
      categoryId: service.categoryId || '',
    });
    setFormCategoryName('');
    setFormOpen(true);
  };

  const persistCategory = async (name, locationId) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return null;
    const result = await api.createWorkspaceServiceCategory({
      name: trimmed,
      locationId,
    });
    if (result.error) {
      setError(result.error);
      return null;
    }
    await refresh();
    await loadServices();
    return result.data;
  };

  const addCategoryFromBar = async (event) => {
    event.preventDefault();
    if (!activeLocation?.id || !newCategoryName.trim()) return;
    setAddingCategory(true);
    const created = await persistCategory(newCategoryName, activeLocation.id);
    setAddingCategory(false);
    if (created) {
      setNewCategoryName('');
      setSelectedCategoryId(created.id);
    }
  };

  const addCategoryFromForm = async () => {
    if (!activeLocation?.id || !formCategoryName.trim()) return;
    setAddingCategory(true);
    const created = await persistCategory(formCategoryName, activeLocation.id);
    setAddingCategory(false);
    if (created) {
      setForm((prev) => ({ ...prev, categoryId: created.id }));
      setFormCategoryName('');
    }
  };

  const saveCategoryName = async (category) => {
    const nextName = editingCategoryName.trim();
    if (!nextName || nextName === category.name) {
      setEditingCategoryId(null);
      return;
    }
    const result = await api.updateWorkspaceServiceCategory(category.id, {
      name: nextName,
      locationId: activeLocation.id,
    });
    if (result.error) {
      setError(result.error);
      return;
    }
    setEditingCategoryId(null);
    await refresh();
    await loadServices();
  };

  const removeCategory = async (category) => {
    const result = await api.deleteWorkspaceServiceCategory(category.id, activeLocation.id);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (form.categoryId === category.id) {
      setForm((prev) => ({ ...prev, categoryId: '' }));
    }
    if (selectedCategoryId === category.id) {
      setSelectedCategoryId('all');
    }
    await refresh();
    await loadServices();
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    let categoryId = form.categoryId || null;
    if (!categoryId && formCategoryName.trim()) {
      const created = await persistCategory(formCategoryName, activeLocation?.id);
      if (!created) {
        setSaving(false);
        return;
      }
      categoryId = created.id;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      durationMinutes: Number(form.durationMinutes) || 60,
      price: Number(form.price) || 0,
      currency: defaultCurrency,
      categoryId,
      active: editingId
        ? services.find((item) => item.id === editingId)?.active !== false
        : true,
      locationId: activeLocation?.id,
    };

    const result = editingId
      ? await api.updateWorkspaceService(editingId, payload)
      : await api.createWorkspaceService(payload);

    setSaving(false);
    if (result.error) {
      setError(result.error);
      return;
    }

    setFormOpen(false);
    if (categoryId) setSelectedCategoryId(categoryId);
    await loadServices();
  };

  const setServiceActive = async (serviceId, active) => {
    const result = await api.updateWorkspaceService(serviceId, { active });
    if (result.error) {
      setError(result.error);
      return;
    }
    await loadServices();
  };

  const removeService = async (service) => {
    const ok = window.confirm(
      `Delete ${service.name}? It will leave this list and the booking page. Past visits keep the name.`
    );
    if (!ok) return;
    const result = await api.deleteWorkspaceService(service.id, activeLocation?.id);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (editingId === service.id) setFormOpen(false);
    await loadServices();
  };

  const isHidden = (service) => service.active === false;
  const editingService = services.find((item) => item.id === editingId) || null;
  const hiddenCount = services.filter(isHidden).length;
  const uncategorizedCount = services.filter(
    (service) => !service.categoryId || !categories.some((item) => item.id === service.categoryId)
  ).length;
  const selectedCategory = categories.find((item) => item.id === selectedCategoryId) || null;
  const visibleServices = useMemo(() => {
    const inScope =
      selectedCategoryId === 'all'
        ? services
        : selectedCategoryId === 'hidden'
          ? services.filter(isHidden)
          : selectedCategoryId === 'uncategorized'
            ? services.filter(
                (service) =>
                  !service.categoryId || !categories.some((item) => item.id === service.categoryId)
              )
            : services.filter((service) => service.categoryId === selectedCategoryId);
    return [...inScope].sort((a, b) => Number(isHidden(a)) - Number(isHidden(b)));
  }, [services, categories, selectedCategoryId]);

  const listHeading =
    selectedCategoryId === 'all'
      ? 'All services'
      : selectedCategoryId === 'hidden'
        ? 'Hidden'
        : selectedCategoryId === 'uncategorized'
          ? 'Uncategorized'
          : selectedCategory?.name || 'Services';

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-light text-ink-900 sm:text-3xl">Services</h2>
          <p className="mt-1 text-sm text-ink-700/80">
            Duration and price drive availability and checkout at{' '}
            <span className="font-medium text-ink-900">{activeLocation?.name || 'this location'}</span>
            {' · '}
            <span className="font-medium text-ink-900">
              {currencyMeta?.symbol || defaultCurrency} {defaultCurrency}
            </span>
          </p>
          {hasLocaleOverride && (
            <p className="mt-2 text-xs text-ink-600/70">
              Previewing locale <span className="font-semibold text-ink-900">{local}</span> via URL
              (prices show in {defaultCurrency}). Remove{' '}
              <code className="text-ink-800">?locale=</code> to use the business country / phone.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex flex-none items-center justify-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
        >
          <Plus className="h-4 w-4" />
          {selectedCategory ? `Add to ${selectedCategory.name}` : 'Add service'}
        </button>
      </div>

      {error && (
        <div className="mt-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-6 bg-sand-50 p-5 ring-1 ring-ink-900/10 sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">Categories</p>
        <p className="mt-1 text-sm text-ink-700/75">
          Click a category to see its services, add under it, or rename it.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCategoryId('all')}
            className={`px-3 py-1.5 text-sm transition-colors ${
              selectedCategoryId === 'all'
                ? 'bg-ink-900 text-sand-50'
                : 'bg-white text-ink-800 ring-1 ring-ink-900/10 hover:bg-white'
            }`}
          >
            All
          </button>
          {categories.map((category) =>
            editingCategoryId === category.id ? (
              <input
                key={category.id}
                autoFocus
                value={editingCategoryName}
                onChange={(e) => setEditingCategoryName(e.target.value)}
                onBlur={() => saveCategoryName(category)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveCategoryName(category);
                  }
                  if (e.key === 'Escape') setEditingCategoryId(null);
                }}
                className="bg-white px-3 py-1.5 text-sm text-ink-900 ring-1 ring-inset ring-saffron-500 focus:outline-none"
              />
            ) : (
              <span
                key={category.id}
                className={`inline-flex items-center gap-1 ${
                  selectedCategoryId === category.id
                    ? 'bg-ink-900 text-sand-50'
                    : 'bg-white text-ink-800 ring-1 ring-ink-900/10'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedCategoryId(category.id)}
                  className="px-3 py-1.5 text-sm"
                >
                  {category.name}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategoryId(category.id);
                    setEditingCategoryId(category.id);
                    setEditingCategoryName(category.name);
                  }}
                  aria-label={`Rename ${category.name}`}
                  className={`px-1 py-1.5 ${
                    selectedCategoryId === category.id
                      ? 'text-sand-200 hover:text-sand-50'
                      : 'text-ink-500 hover:text-ink-900'
                  }`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeCategory(category)}
                  aria-label={`Remove ${category.name}`}
                  className={`pr-2 py-1.5 ${
                    selectedCategoryId === category.id
                      ? 'text-sand-200 hover:text-red-300'
                      : 'text-ink-500 hover:text-red-700'
                  }`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )
          )}
          {uncategorizedCount > 0 && (
            <button
              type="button"
              onClick={() => setSelectedCategoryId('uncategorized')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                selectedCategoryId === 'uncategorized'
                  ? 'bg-ink-900 text-sand-50'
                  : 'bg-white text-ink-800 ring-1 ring-ink-900/10 hover:bg-white'
              }`}
            >
              Uncategorized
            </button>
          )}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setSelectedCategoryId('hidden')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                selectedCategoryId === 'hidden'
                  ? 'bg-ink-900 text-sand-50'
                  : 'bg-white text-ink-800 ring-1 ring-ink-900/10 hover:bg-white'
              }`}
            >
              Hidden ({hiddenCount})
            </button>
          )}
        </div>
        <form className="mt-4 flex gap-2" onSubmit={addCategoryFromBar}>
          <input
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="e.g. Braids"
            className="min-w-0 flex-1 bg-white px-4 py-2.5 text-sm text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
          />
          <button
            type="submit"
            disabled={addingCategory || !newCategoryName.trim()}
            className="px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white disabled:opacity-50"
          >
            Add
          </button>
        </form>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-display text-xl font-light text-ink-900">{listHeading}</h3>
          <p className="mt-1 text-sm text-ink-700/70">
            {visibleServices.length} {visibleServices.length === 1 ? 'service' : 'services'}
            {selectedCategoryId !== 'hidden' && visibleServices.some(isHidden)
              ? ` · ${visibleServices.filter(isHidden).length} hidden from booking`
              : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-sand-50"
        >
          <Plus className="h-3.5 w-3.5" />
          {selectedCategory
            ? `Add to ${selectedCategory.name}`
            : selectedCategoryId === 'uncategorized'
              ? 'Add uncategorized'
              : 'Add service'}
        </button>
      </div>

      <div className="mt-6 bg-sand-50 ring-1 ring-ink-900/10">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
          </div>
        ) : visibleServices.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="font-display text-2xl font-light text-ink-900">
              {selectedCategoryId === 'hidden'
                ? 'No hidden services'
                : selectedCategory
                  ? `No services in ${selectedCategory.name}`
                  : 'No services'}
            </p>
            <p className="mt-2 text-sm text-ink-700/70">
              {selectedCategoryId === 'hidden'
                ? 'Hidden services stay in Manage but do not appear on your booking page.'
                : selectedCategory
                  ? `Add a service to ${selectedCategory.name} to show it on your booking page.`
                  : 'Add at least one service to take bookings.'}
            </p>
            {selectedCategoryId !== 'hidden' && (
              <button
                type="button"
                onClick={openCreate}
                className="mt-6 inline-flex items-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 hover:bg-saffron-400"
              >
                <Plus className="h-4 w-4" />
                {selectedCategory ? `Add to ${selectedCategory.name}` : 'Add service'}
              </button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-ink-900/10">
            {visibleServices.map((service) => {
              const menuOpen = menuOpenId === service.id;
              return (
                <li
                  key={service.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit ${service.name}`}
                  className={`flex cursor-pointer items-start justify-between gap-1 hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-saffron-500 ${
                    isHidden(service) ? 'bg-ink-900/[0.03]' : ''
                  }`}
                  onClick={() => openEdit(service)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openEdit(service);
                    }
                  }}
                >
                  <div className="min-w-0 flex-1 px-5 py-4">
                    <p className="flex flex-wrap items-center gap-2 font-medium text-ink-900">
                      {service.name}
                      {categoryNameById(service.categoryId) && (
                        <span className="bg-ink-900/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-600/70">
                          {categoryNameById(service.categoryId)}
                        </span>
                      )}
                      {isHidden(service) && (
                        <span className="bg-ink-900/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-600/80">
                          Hidden
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-sm text-ink-700/75">
                      {formatDuration(service.durationMinutes)} ·{' '}
                      {formatCurrency(service.price, defaultCurrency)}
                      {isHidden(service) ? ' · Off the booking page' : ''}
                    </p>
                    {service.description && (
                      <p className="mt-1 text-sm text-ink-600/70">{service.description}</p>
                    )}
                  </div>
                  <div
                    className="relative flex-none py-3 pr-3"
                    data-service-menu
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      aria-label={`Actions for ${service.name}`}
                      aria-expanded={menuOpen}
                      onClick={() => setMenuOpenId(menuOpen ? null : service.id)}
                      className="p-2 text-ink-600 hover:bg-white hover:text-ink-900"
                    >
                      <MoreHorizontal className="h-5 w-5" />
                    </button>
                    {menuOpen && (
                      <div className="absolute right-0 z-20 mt-1 min-w-[11rem] bg-sand-50 py-1 shadow-[0_16px_40px_-24px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10">
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpenId(null);
                            openEdit(service);
                          }}
                          className="block w-full px-4 py-2.5 text-left text-sm text-ink-900 hover:bg-white"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpenId(null);
                            setServiceActive(service.id, isHidden(service));
                          }}
                          className="block w-full px-4 py-2.5 text-left text-sm text-ink-900 hover:bg-white"
                        >
                          {isHidden(service) ? 'Unhide' : 'Hide from booking'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpenId(null);
                            removeService(service);
                          }}
                          className="block w-full px-4 py-2.5 text-left text-sm text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-ink-950/50 p-3 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full max-w-md overflow-y-auto bg-sand-50 p-5 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10 sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-light text-ink-900">
                  {editingId ? 'Edit service' : 'Add service'}
                </h2>
                {editingService && isHidden(editingService) && (
                  <p className="mt-1 text-sm text-ink-600/70">Hidden from the booking page</p>
                )}
              </div>
              <button type="button" onClick={() => setFormOpen(false)} aria-label="Close">
                <X className="h-5 w-5 text-ink-700" />
              </button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSave}>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Name
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="Silk press"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Category
                </label>
                <select
                  value={form.categoryId}
                  onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                >
                  <option value="">No category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <div className="mt-2 flex gap-2">
                  <input
                    value={formCategoryName}
                    onChange={(e) => setFormCategoryName(e.target.value)}
                    placeholder="Or create a new category"
                    className="min-w-0 flex-1 bg-white px-4 py-2.5 text-sm text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  />
                  <button
                    type="button"
                    onClick={addCategoryFromForm}
                    disabled={addingCategory || !formCategoryName.trim()}
                    className="px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Duration (min)
                  </label>
                  <input
                    type="number"
                    min={5}
                    step={5}
                    required
                    value={form.durationMinutes}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, durationMinutes: e.target.value }))
                    }
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Price ({defaultCurrency})
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={defaultCurrency === 'GHS' || defaultCurrency === 'USD' ? 1 : 100}
                    value={form.price}
                    onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add service'}
              </button>
              {editingService && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setServiceActive(editingService.id, isHidden(editingService))
                    }
                    className="min-w-0 flex-1 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white"
                  >
                    {isHidden(editingService) ? 'Unhide' : 'Hide from booking'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeService(editingService)}
                    className="min-w-0 flex-1 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-red-700 ring-1 ring-red-200 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageServices;
